import { Card, Row, Col, Typography, Progress, Tooltip } from "antd";
import { useMemo } from "react";
import dayjs from "dayjs";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, ArcElement, PointElement, Tooltip as TooltipChard, Legend } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, ArcElement, PointElement, TooltipChard, Legend);
const { Text } = Typography;
const COLORS = {
  primary: "#328BC7",
  darkBlue: "#042956",
  danger: "#DC001A",
  warning: "#F3A200",
  success: "#52C41A",
  peach: "#FFDAB9",
  white: "#FFFFFF",
  textDark: "#042956",
  textLight: "#FFFFFF"
};



export default function DashboardCharts({ stock = [], movimientos = [] }) {

  const topStockProductos = useMemo(() => {
    if (!stock.length) return { labels: [], datasets: [] };
    const sorted = [...stock]
      .sort(
        (a, b) =>
          Number(b.stock_disponible || 0) -
          Number(a.stock_disponible || 0)
      )
      .slice(0, 8);
    return {
      labels: sorted.map(p =>
        (p.nombre_producto || "Producto").slice(0, 22) + "..."
      ),
      datasets: [
        {
          label: "Stock disponible",
          data: sorted.map(p =>
            Number(p.stock_disponible || 0)
          ),
          backgroundColor: COLORS.primary,
          borderRadius: 6,
          barThickness: 18,
          datalabels: {
            color: "#FFFFFF",
            anchor: "center",
            align: "center",
            font: {
              weight: "bold",
              size: 12
            }
          }
        }
      ]
    };
  }, [stock]);

  const ESTADOS_STOCK = {
    NORMAL: {
      label: "Disponible",
      descripcion: "Hay suficiente stock disponible para las entregas.",
      color: COLORS.primary
    },
    BAJO: {
      label: "Parcial",
      descripcion: "El stock es limitado y puede no cubrir todas las entregas.",
      color: COLORS.peach
    },
    AGOTADO: {
      label: "Agotado",
      descripcion: "No hay unidades disponibles en inventario.",
      color: COLORS.success
    },
    CRITICO: {
      label: "Crítico",
      descripcion: "El stock está por agotarse y requiere reposición urgente.",
      color: COLORS.danger
    }
  };

  const distribucionStock = useMemo(() => {

    if (!stock.length) return { labels: [], datasets: [] };

    const estados = {};

    stock.forEach(p => {

      const estado = (p.estado_stock || "NORMAL").toUpperCase();

      if (!estados[estado]) estados[estado] = 0;

      estados[estado]++;

    });

    const labels = Object.keys(estados).map(e => ESTADOS_STOCK[e]?.label || e);

    const colors = Object.keys(estados).map(e => ESTADOS_STOCK[e]?.color || COLORS.primary);

    return {
      labels,
      datasets: [
        {
          data: Object.values(estados),
          backgroundColor: colors,
          borderWidth: 0,
          datalabels: {
            color: "#FFFFFF",
            font: {
              weight: "bold",
              size: 14
            }
          }
        }
      ]
    };

  }, [stock]);

  const entregasMes = useMemo(() => {
    if (!movimientos.length) return { labels: [], datasets: [] };
    const map = {};
    movimientos.forEach(m => {
      const fecha =
        m.fecha_entrega ||
        m.fecha ||
        m.created_at;
      if (!fecha) return;
      const mes = dayjs(fecha).format("YYYY-MM");
      const cantidad = Number(
        m.cantidad_entregada ||
        m.cantidad ||
        0
      );
      map[mes] = Math.round((map[mes] || 0) + cantidad);
    });

    const sorted = Object.entries(map)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]));
    return {
      labels: sorted.map(i => i[0]),
      datasets: [
        {
          label: "Entregas",
          data: sorted.map(i => i[1]),
          borderColor: COLORS.primary,
          backgroundColor: "rgba(50,139,199,0.15)",
          tension: 0.35,
          borderWidth: 3,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: COLORS.darkBlue
        }
      ]
    };
  }, [movimientos]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `Unidades: ${formatCompactNumber(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: COLORS.textDark,
          callback: (value) => formatCompactNumber(value)
        }
      },
      y: {
        grid: { display: false },
        ticks: { color: COLORS.textDark }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {

      legend: {
        position: "right",
        labels: {
          color: COLORS.textDark
        }
      },

      tooltip: {
        callbacks: {

          label: function (context) {

            const label = context.label;
            const value = context.raw;

            const estado = Object.values(ESTADOS_STOCK).find(e => e.label === label);

            return [
              `${label}: ${value} productos`,
              estado?.descripcion || ""
            ];

          }

        }
      }

    }
  };



  const formatCompactNumber = (value) => {
    if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
    if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
    if (value >= 1_000) return (value / 1_000).toFixed(1) + "K";
    return value;
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: COLORS.textDark
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `Entregas: ${formatCompactNumber(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: COLORS.textDark }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: COLORS.textDark,
          callback: (value) => formatCompactNumber(value)
        }
      }
    }
  };
  const formatCompact = (num) => {
    const n = Number(num) || 0;
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return Math.round(n);
  };


  const progresoDepartamentos = useMemo(() => {
    const map = {};
    stock.forEach(p => {
      const dept = p.departamento || "Sin dato";
      const total =
        Number(p.cantidad_entregada || 0) +
        Number(p.stock_disponible || 0);
      if (!map[dept]) {
        map[dept] = { total: 0, entregado: 0 };
      }
      map[dept].total = Math.round(map[dept].total + total);
    });
    movimientos.forEach(m => {
      const dept = m.departamento || "Sin dato";
      const entregado = Number(
        m.cantidad_entregada ||
        m.cantidad ||
        0
      );
      if (!map[dept]) {
        map[dept] = { total: 0, entregado: 0 };
      }
      map[dept].entregado = Math.round(map[dept].entregado + entregado);
    });
    return Object.entries(map)
      .map(([dept, data]) => {
        const porcentaje = data.total
          ? Math.round((data.entregado / data.total) * 100)
          : 0;
        return {
          departamento: dept,
          entregado: data.entregado,
          total: data.total,
          porcentaje
        };
      })
      .sort((a, b) => b.porcentaje - a.porcentaje)
      .slice(0, 8);
  }, [stock, movimientos]);

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 24, marginBottom: 24 }}>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Stock por Producto</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Top disponibles</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240 }}>
            <Bar
              data={topStockProductos}
              options={barOptions}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Estado del Stock</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Distribución inventario</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240, padding: 12 }}>
            <Doughnut
              data={distribucionStock}
              options={pieOptions}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Entregas por Departamento</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Top departamentos</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div
            style={{
              padding: "12px 6px",
              borderRadius: 8
            }}
          >
            {progresoDepartamentos.map((d, index) => (
              <Tooltip
                key={index}
                title={
                  <>
                    <div><b>{d.departamento}</b></div>
                    <div>Entregados: {Math.round(d.entregado).toLocaleString("es-CO")}</div>
                    <div>Total disponible: {Math.round(d.total).toLocaleString("es-CO")}</div>
                    <div>Progreso: {d.porcentaje}%</div>
                  </>
                }
              >

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6
                  }}
                >
                  <Text strong style={{ color: COLORS.textDark }}>
                    {d.departamento}
                  </Text>
                  <Text type="secondary">
                    {formatCompact(d.entregado)} / {formatCompact(d.total)}
                  </Text>
                </div>
                <Progress
                  percent={d.porcentaje}
                  strokeColor={COLORS.primary}
                  trailColor="#f0f0f0"
                  strokeWidth={10}
                />
              </Tooltip>
            ))}
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Tendencia de Entregas</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Evolución mensual</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 420, padding: 12 }}>
            <Line
              data={entregasMes}
              options={lineOptions}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );

}